/**
 * Shared status pill — used in BOTH the pipelines list table and the
 * detail page header. Single source of truth for the bundle-state +
 * warm-pool combined runtime experience:
 *
 *   bundleStatus='ready'    + warm  → Live      (green pulse)
 *   bundleStatus='ready'    + !warm → Sleeping  (gray + animated zz)
 *   bundleStatus='building'         → Building  (amber pulse)
 *   bundleStatus='failed'           → Degraded  (red, fix-me)
 *   bundleStatus=null|undef         → Sleeping  (mid-deploy / legacy)
 *
 * `variant` switches the CSS class prefix so the same logic renders
 * consistently in both contexts (table cell vs header subtitle), each
 * with its own typographic scale.
 *   variant="table"  → uses .pipelines-status-* (defined in
 *                       PipelinesPage.css)
 *   variant="header" → uses .pipeline-detail-status-* (defined in
 *                       PipelineDetailPage.css)
 *
 * Pure presentation — no data fetching, no state. Caller passes the
 * raw fields from the workflow row.
 */
import React from 'react';

const STATUS_TEXT = {
  live:     'Live',
  running:  'Running',
  starting: 'Starting',
  sleeping: 'Sleeping',
  building: 'Building',
  failed:   'Degraded',
  // Count-carrying: "Waiting on 1 agent" / "Waiting on 3 agents". The word is
  // resolved by statusText() below, because the number is part of the claim.
  waiting:  'Waiting',
};

/**
 * The pill's words. Every state but one is a fixed label; `waiting` names HOW
 * MANY agents this one is waiting on, because "Waiting" alone would raise the
 * question it exists to answer.
 */
export function statusText(key, { memberRuns = 0 } = {}) {
  if (key !== 'waiting') return STATUS_TEXT[key];
  const n = Math.max(0, Math.floor(Number(memberRuns) || 0));
  return `Waiting on ${n} agent${n === 1 ? '' : 's'}`;
}

export function resolveStatusKey({ bundleStatus, warmEnabled, warmProvisioning, activeRuns, chatRuntime, memberRuns = 0 }) {
  if (bundleStatus === 'building') return 'building';
  if (bundleStatus === 'failed')   return 'failed';
  // Chat/Lambda-hosted Copilot (chatRuntime): the agent runs on a Lambda kept
  // warm by an EventBridge ping — it is ALWAYS available. "Sleeping" is a
  // Fargate warm-pool concept (there's no cold container to wake) and reads as
  // misleading/broken here, so a Copilot is never "Sleeping": it's "Running"
  // while a turn is in flight, otherwise "Live". This branch sits above the
  // warmEnabled logic so warmEnabled (never set on a chat agent) can't drag it
  // back to Sleeping, and below the bundleStatus checks so a mid-deploy
  // "Building" / "Degraded" still shows correctly.
  if (chatRuntime) return Number(activeRuns) > 0 ? 'running' : 'live';
  // "Running" wins over everything below: a run is executing RIGHT NOW (a
  // trigger is mid-flight on a Fargate task). Without this, a warm-OFF agent
  // stays "Sleeping" the whole time it's actually working — which reads as
  // "nothing happened" the moment you hit Trigger. `activeRuns` is the count of
  // in-flight executions (queued/starting/running) the backend attaches.
  if (Number(activeRuns) > 0) return 'running';
  // ── WAITING ON THE AGENTS IT STARTED ────────────────────────────────────
  // A composite agent (a wrapper like board-runner) dispatches its members and
  // its OWN run ENDS — so between ticks it has activeRuns:0 while the workers
  // it just started are mid-flight, and the pill read "Sleeping" beside a
  // canvas full of running members (founder, 2026-08-26). That is false: the
  // agent is not asleep, it is waiting. Below "Running" (a run of its own
  // always wins) and above the warm-pool states, which describe a runner that
  // is standing by — not one whose work is out with somebody else.
  // `memberRuns` is the SAME activeRuns the member satellites' paper stacks are
  // drawn from (utils/agentComposition memberRunsTotal); an agent that starts
  // nothing passes 0 and can never reach this state.
  if (Number(memberRuns) > 0) return 'waiting';
  // "Live" means a warm runner is standing by (instant, no cold-start) —
  // warmEnabled is what drives it, NOT whether a bundle was ever deployed.
  //   warm on  + runner up          → Live
  //   warm on  + still provisioning  → Starting
  //   warm OFF (deployed or not)     → Sleeping  (cold-starts on trigger)
  // A deployed-but-cold pipeline is "Sleeping" (it wakes on demand), so
  // turning warm OFF flips the pill Live → Sleeping as the user expects.
  if (warmEnabled && warmProvisioning) return 'starting';
  if (warmEnabled) return 'live';
  return 'sleeping';
}

const PipelineStatus = ({ bundleStatus, warmEnabled, warmProvisioning, activeRuns, chatRuntime = false, memberRuns = 0, variant = 'table' }) => {
  const key = resolveStatusKey({ bundleStatus, warmEnabled, warmProvisioning, activeRuns, chatRuntime, memberRuns });
  const text = statusText(key, { memberRuns });
  const prefix = variant === 'header' ? 'pipeline-detail-status' : 'pipelines-status';

  return (
    <span className={`${prefix} ${prefix}-${key}`}>
      <span className={`${prefix}-dot`} />
      {text}
      {/* Animated zz only on Sleeping — both variants opt in via the
          shared .pipelines-status-zz class so the floating Z's look
          identical in table and header (font-size differs naturally
          via the parent variant's color/size). */}
      {key === 'sleeping' && (
        <span className="pipelines-status-zz" aria-hidden="true">
          <span className="pipelines-zz pipelines-zz-1">z</span>
          <span className="pipelines-zz pipelines-zz-2">z</span>
        </span>
      )}
    </span>
  );
};

export default PipelineStatus;
